import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Wallet, 
  CheckCircle, 
  AlertCircle, 
  Building2,
  CreditCard,
  Shield,
  ArrowRight,
  X,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase-client';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

interface BankDetails {
  accountNumber: string;
  accountName: string;
  bankName: string;
  bankCode: string;
}

const NIGERIAN_BANKS = [
  { name: 'Access Bank', code: '044' },
  { name: 'Citibank', code: '023' },
  { name: 'Ecobank Nigeria', code: '050' },
  { name: 'Fidelity Bank', code: '070' },
  { name: 'First Bank of Nigeria', code: '011' },
  { name: 'First City Monument Bank (FCMB)', code: '214' },
  { name: 'Globus Bank', code: '00103' },
  { name: 'Guaranty Trust Bank (GTBank)', code: '058' },
  { name: 'Heritage Bank', code: '030' },
  { name: 'Keystone Bank', code: '082' },
  { name: 'Kuda Bank', code: '50211' },
  { name: 'Opay', code: '999992' },
  { name: 'PalmPay', code: '999991' },
  { name: 'Polaris Bank', code: '076' },
  { name: 'Providus Bank', code: '101' },
  { name: 'Stanbic IBTC Bank', code: '221' },
  { name: 'Standard Chartered Bank', code: '068' },
  { name: 'Sterling Bank', code: '232' },
  { name: 'Union Bank of Nigeria', code: '032' },
  { name: 'United Bank for Africa (UBA)', code: '033' },
  { name: 'Unity Bank', code: '215' },
  { name: 'Wema Bank', code: '035' },
  { name: 'Zenith Bank', code: '057' }
];

interface ReferralAccountSetupProps {
  onComplete: () => void;
  existingDetails?: BankDetails;
  mode?: 'setup' | 'update';
}

export const ReferralAccountSetup: React.FC<ReferralAccountSetupProps> = ({ 
  onComplete, 
  existingDetails,
  mode = 'setup'
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountNumber: existingDetails?.accountNumber || '',
    accountName: existingDetails?.accountName || '',
    bankName: existingDetails?.bankName || '',
    bankCode: existingDetails?.bankCode || ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateAccountNumber = (number: string): boolean => {
    return /^\d{10}$/.test(number);
  };

  const validateAccountName = (name: string): boolean => {
    return name.trim().length >= 3;
  };

  const handleBankChange = (bankName: string) => {
    console.log('Bank selected:', bankName);
    const selectedBank = NIGERIAN_BANKS.find(b => b.name === bankName);
    if (selectedBank) {
      setBankDetails({
        ...bankDetails,
        bankName: selectedBank.name,
        bankCode: selectedBank.code
      });
      setErrors({ ...errors, bankName: '' });
      toast.success(`✓ ${selectedBank.name} selected`);
    }
  };

  const handleAccountNumberChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').substring(0, 10);
    setBankDetails({ ...bankDetails, accountNumber: cleaned });
    
    if (cleaned.length === 10) {
      setErrors({ ...errors, accountNumber: '' });
    }
  };

  const handleAccountNameChange = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z\s]/g, '');
    setBankDetails({ ...bankDetails, accountName: cleaned });
    
    if (cleaned.length >= 3) {
      setErrors({ ...errors, accountName: '' });
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!bankDetails.bankName) {
      newErrors.bankName = 'Please select your bank';
    }

    if (!validateAccountNumber(bankDetails.accountNumber)) {
      newErrors.accountNumber = 'Account number must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!validateAccountName(bankDetails.accountName)) {
      newErrors.accountName = 'Account name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyAccount = async () => {
    if (!validateStep1()) {
      return;
    }

    setVerifying(true);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setVerifying(false);
    setStep(2);
    toast.success('Account verified! Please confirm your name.');
  };

  const handleSubmit = async () => {
    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to continue');
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/referral/setup-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session.access_token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ bankDetails })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success(mode === 'setup' ? '🎉 Account Setup Complete!' : '✅ Account Updated!', {
          description: 'You can now start earning through referrals'
        });
        onComplete();
      } else {
        toast.error(data.error || 'Failed to setup account');
      }
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const FormContent = () => (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white mb-4">
          <Wallet className="w-8 h-8" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          {mode === 'setup' ? 'Setup Referral Account' : 'Update Bank Details'}
        </h1>
        <p className="text-gray-600">
          {mode === 'setup' 
            ? 'Add your bank details to start earning from referrals'
            : 'Update your bank account information'
          }
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <StepIndicator 
          number={1} 
          label="Bank Details" 
          active={step === 1} 
          completed={step > 1}
        />
        <div className={`h-1 w-16 ${step > 1 ? 'bg-green-500' : 'bg-gray-300'} rounded`}></div>
        <StepIndicator 
          number={2} 
          label="Confirm Name" 
          active={step === 2} 
          completed={step > 2}
        />
      </div>

      {/* Steps */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Bank Selection */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-green-600" />
                Select Your Bank
              </label>
              <div className="relative">
                <select
                  value={bankDetails.bankName}
                  onChange={(e) => handleBankChange(e.target.value)}
                  className={`w-full px-4 py-4 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-white text-base cursor-pointer appearance-none transition-all ${
                    errors.bankName ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <option value="">Choose your bank...</option>
                  {NIGERIAN_BANKS.map(bank => (
                    <option key={bank.code} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {errors.bankName && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.bankName}
                </p>
              )}
            </div>

            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-green-600" />
                Account Number
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={bankDetails.accountNumber}
                onChange={(e) => handleAccountNumberChange(e.target.value)}
                placeholder="0123456789"
                maxLength={10}
                className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-base transition-all ${
                  errors.accountNumber ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                }`}
              />
              <p className="text-xs text-gray-500 mt-1">
                {bankDetails.accountNumber.length}/10 digits
              </p>
              {errors.accountNumber && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.accountNumber}
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Your information is secure</p>
                <p className="text-blue-700">
                  We only use your bank details for referral payouts. Your data is encrypted and never shared.
                </p>
              </div>
            </div>

            {/* Next Button */}
            <Button
              onClick={handleVerifyAccount}
              disabled={verifying || !bankDetails.bankName || !bankDetails.accountNumber}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verifying Account...
                </>
              ) : (
                <>
                  Verify Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Bank Details Summary */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-sm text-green-700 mb-2">Account Details:</p>
              <p className="font-semibold text-green-900">{bankDetails.bankName}</p>
              <p className="text-green-800">{bankDetails.accountNumber}</p>
            </div>

            {/* Account Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Account Name (as shown on your bank statement)
              </label>
              <input
                type="text"
                value={bankDetails.accountName}
                onChange={(e) => handleAccountNameChange(e.target.value)}
                placeholder="JOHN DOE"
                className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none uppercase text-base transition-all ${
                  errors.accountName ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                }`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your name exactly as it appears on your bank account
              </p>
              {errors.accountName && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.accountName}
                </p>
              )}
            </div>

            {/* Warning Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Important!</p>
                <p className="text-amber-700">
                  Make sure the name matches your bank account. Incorrect names may cause withdrawal delays.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 py-6"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !bankDetails.accountName}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl"
      >
        <Card className="p-6 md:p-8 shadow-2xl">
          <FormContent />
        </Card>
      </motion.div>
    </div>
  );
};

interface StepIndicatorProps {
  number: number;
  label: string;
  active: boolean;
  completed: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ number, label, active, completed }) => (
  <div className="flex flex-col items-center">
    <div className={`
      w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mb-2 transition-all
      ${completed ? 'bg-green-500 text-white' : active ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}
    `}>
      {completed ? <CheckCircle className="w-5 h-5" /> : number}
    </div>
    <p className={`text-xs ${active || completed ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
      {label}
    </p>
  </div>
);

// Modal component
interface ReferralAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  existingDetails?: BankDetails;
}

export const ReferralAccountModal: React.FC<ReferralAccountModalProps> = ({ 
  isOpen, 
  onClose, 
  onComplete,
  existingDetails 
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountNumber: existingDetails?.accountNumber || '',
    accountName: existingDetails?.accountName || '',
    bankName: existingDetails?.bankName || '',
    bankCode: existingDetails?.bankCode || ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const mode = existingDetails ? 'update' : 'setup';

  const validateAccountNumber = (number: string): boolean => {
    return /^\d{10}$/.test(number);
  };

  const validateAccountName = (name: string): boolean => {
    return name.trim().length >= 3;
  };

  const handleBankChange = (bankName: string) => {
    console.log('Bank selected:', bankName);
    const selectedBank = NIGERIAN_BANKS.find(b => b.name === bankName);
    if (selectedBank) {
      setBankDetails({
        ...bankDetails,
        bankName: selectedBank.name,
        bankCode: selectedBank.code
      });
      setErrors({ ...errors, bankName: '' });
      toast.success(`✓ ${selectedBank.name} selected`);
    }
  };

  const handleAccountNumberChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').substring(0, 10);
    setBankDetails({ ...bankDetails, accountNumber: cleaned });
    
    if (cleaned.length === 10) {
      setErrors({ ...errors, accountNumber: '' });
    }
  };

  const handleAccountNameChange = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z\s]/g, '');
    setBankDetails({ ...bankDetails, accountName: cleaned });
    
    if (cleaned.length >= 3) {
      setErrors({ ...errors, accountName: '' });
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!bankDetails.bankName) {
      newErrors.bankName = 'Please select your bank';
    }

    if (!validateAccountNumber(bankDetails.accountNumber)) {
      newErrors.accountNumber = 'Account number must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!validateAccountName(bankDetails.accountName)) {
      newErrors.accountName = 'Account name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyAccount = async () => {
    if (!validateStep1()) {
      return;
    }

    setVerifying(true);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setVerifying(false);
    setStep(2);
    toast.success('Account verified! Please confirm your name.');
  };

  const handleSubmit = async () => {
    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to continue');
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/referral/setup-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-Supabase-Token': session.access_token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ bankDetails })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success(mode === 'setup' ? '🎉 Account Setup Complete!' : '✅ Account Updated!', {
          description: 'You can now start earning through referrals'
        });
        onComplete();
        onClose();
      } else {
        toast.error(data.error || 'Failed to setup account');
      }
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-2xl my-8"
      >
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white mb-4">
                <Wallet className="w-8 h-8" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                {mode === 'setup' ? 'Setup Referral Account' : 'Update Bank Details'}
              </h1>
              <p className="text-gray-600">
                {mode === 'setup' 
                  ? 'Add your bank details to start earning from referrals'
                  : 'Update your bank account information'
                }
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <StepIndicator 
                number={1} 
                label="Bank Details" 
                active={step === 1} 
                completed={step > 1}
              />
              <div className={`h-1 w-16 ${step > 1 ? 'bg-green-500' : 'bg-gray-300'} rounded`}></div>
              <StepIndicator 
                number={2} 
                label="Confirm Name" 
                active={step === 2} 
                completed={step > 2}
              />
            </div>

            {/* Steps */}
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Bank Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-green-600" />
                      Select Your Bank
                    </label>
                    <div className="relative">
                      <select
                        value={bankDetails.bankName}
                        onChange={(e) => handleBankChange(e.target.value)}
                        className={`w-full px-4 py-4 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-white text-base cursor-pointer appearance-none transition-all ${
                          errors.bankName ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <option value="">Choose your bank...</option>
                        {NIGERIAN_BANKS.map(bank => (
                          <option key={bank.code} value={bank.name}>
                            {bank.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    {errors.bankName && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.bankName}
                      </p>
                    )}
                  </div>

                  {/* Account Number */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-green-600" />
                      Account Number
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={bankDetails.accountNumber}
                      onChange={(e) => handleAccountNumberChange(e.target.value)}
                      placeholder="0123456789"
                      maxLength={10}
                      className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-base transition-all ${
                        errors.accountNumber ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {bankDetails.accountNumber.length}/10 digits
                    </p>
                    {errors.accountNumber && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.accountNumber}
                      </p>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-1">Your information is secure</p>
                      <p className="text-blue-700">
                        We only use your bank details for referral payouts. Your data is encrypted and never shared.
                      </p>
                    </div>
                  </div>

                  {/* Next Button */}
                  <Button
                    onClick={handleVerifyAccount}
                    disabled={verifying || !bankDetails.bankName || !bankDetails.accountNumber}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying Account...
                      </>
                    ) : (
                      <>
                        Verify Account
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Bank Details Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm text-green-700 mb-2">Account Details:</p>
                    <p className="font-semibold text-green-900">{bankDetails.bankName}</p>
                    <p className="text-green-800">{bankDetails.accountNumber}</p>
                  </div>

                  {/* Account Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Account Name (as shown on your bank statement)
                    </label>
                    <input
                      type="text"
                      value={bankDetails.accountName}
                      onChange={(e) => handleAccountNameChange(e.target.value)}
                      placeholder="JOHN DOE"
                      className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none uppercase text-base transition-all ${
                        errors.accountName ? 'border-red-500' : 'border-gray-300 hover:border-green-300'
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter your name exactly as it appears on your bank account
                    </p>
                    {errors.accountName && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.accountName}
                      </p>
                    )}
                  </div>

                  {/* Warning Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <p className="font-semibold mb-1">Important!</p>
                      <p className="text-amber-700">
                        Make sure the name matches your bank account. Incorrect names may cause withdrawal delays.
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 py-6"
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={loading || !bankDetails.accountName}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Complete Setup
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};